import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NSCommons } from '@ruby/configs/common.constants';
import { NSUrlsConstants } from '@ruby/configs/urls.constants';
import {
  IECRCalendar,
  IECRCancel,
  IECRCancelRequest,
  IECRCar,
  IECRCarRequest,
  IECRCommodity,
  IECRGridMetric,
  IECRLocationSummary,
  IECRMetric,
  IECRPattern,
  IECRRequest,
  IECRStatusDetail,
  IECRSubmitResponse
} from '@ruby/shared/models/ecr/ecr.interface';
import { ECRSummaryUtils } from '@ruby/shared/services/utils/ecr/ecr-summary.utils';

@Injectable({
  providedIn: 'root'
})
export class ECRService {

  constructor(private http: HttpClient, private ecrSummaryUtils: ECRSummaryUtils) { }

  /**
   * Fetch Patterns
   *
   * @summary Gets ECR Patterns list
   * returns Observable<Array<IECRPattern> | never>
   */
  fetchPatterns(): Observable<Array<IECRPattern> | never> {
    return this.http.get(NSUrlsConstants.ecrUserDataUrl).pipe(map(response => response as Array<IECRPattern>));
  }

  /**
   * Fetch Commodity
   *
   * @summary Gets Pattern Commodity Info
   * @param patternInfo: Partial<IECRPattern>
   * @returns Observable<IECRCommodity | never>
   */
  fetchCommodity(patternInfo: Partial<IECRPattern>): Observable<IECRCommodity | never> {
    return this.http.post(NSUrlsConstants.ecrCommodityUrl, patternInfo).pipe(map(response => response as IECRCommodity));
  }

  /**
   * Fetch All Requests
   *
   * @summary Gets All Requests
   * @params payload: { startDate: number; endDate: number; carRequest: Array<Pick<IECRPattern, 'classCode' | 'locationId' | 'opStaCode' | 'patterns' | 'servingRoad'>> }
   * @returns Observable<Array<IECRRequest> | never>
   */
  fetchAllRequests(
    payload: {
      startDate: number;
      endDate: number;
      carRequest: Array<Pick<IECRPattern, 'classCode' | 'locationId' | 'opStaCode' | 'patterns' | 'servingRoad'>>;
    }): Observable<Array<IECRRequest> | never> {
    return this.http.post(NSUrlsConstants.ecrAllRequestsUrl, payload).pipe(map(response => response as Array<IECRRequest>));
  }

  /**
   * Fetch Metrics
   *
   * @summary Get ecr metrics depending on the selected pattern
   * @param payload: IECRPattern
   * @return Observable<Array<IECRMetric> | never>
   */
  fetchMetrics(payload: IECRPattern): Observable<IECRMetric> {
    return this.http.post(NSUrlsConstants.ecrMetricsUrl, {
      classCode: payload.classCode,
      customerName: payload.companyName,
      customerPatternName: payload.patternName!,
      opsta: payload.opStaCode,
      servingRoad: payload.servingRoad
    }).pipe(map(response => (
      NSCommons.formatPayload(response as Array<{
        accepted: number;
        badOrder: number;
        eventDate: number;
        loadFill: number;
        orderFill: number;
        rejectedEvents: number;
        releasedEmpty: number;
        resetCount: number;
        totalAccepted: number;
      }>)
    )));
  }

  /**
   * Fetch Grid Metrics
   *
   * @summary Get ecr grid metrics depending on the selected pattern
   * @param payload: IECRPattern
   * @return Observable<Array<IECRGridMetric> | never>
   */
  fetchGridMetrics(payload: IECRPattern): Observable<Array<IECRGridMetric> | never> {
    return this.http.post(NSUrlsConstants.ecrMetricsGridUrl, {
      classCode: payload.classCode,
      customerName: payload.companyName,
      customerPatternName: payload.patternName!,
      opsta: payload.opStaCode,
      servingRoad: payload.servingRoad
    }).pipe(map(response => response as Array<IECRGridMetric>));
  }

  /**
   * Fetch Request Status
   *
   * @summary Get ecr request status depending on the selected patterns
   * @param payload: Array<Pick<IECRPattern, 'classCode' | 'locationId' | 'opStaCode' | 'patterns' | 'servingRoad'>>
   * @return Observable<IECRLocationSummary | never>
   */
  fetchRequestStatus(payload: Array<Pick<IECRPattern, 'classCode' | 'locationId' | 'opStaCode' | 'patterns' | 'servingRoad'>>): Observable<IECRLocationSummary | never> {
    return this.http.post(NSUrlsConstants.ecrRequestStatusUrl, { locationsRequest: payload }).pipe(map(response => response as IECRLocationSummary));
  }

  /**
   * Fetch Current Waybill
   *
   * @summary Get current waybill based on requestId
   * @param payload: number
   * @return Observable<Array<IECRStatusDetail> | never>
   */
  fetchCurrentWaybill(payload: { requestId: number }): Observable<Array<IECRStatusDetail> | never> {
    const params = new HttpParams().set('requestId', payload.requestId.toString());
    return this.http.get(NSUrlsConstants.ecrCurrentWaybillUrl, { params }).pipe(map(response => response as Array<IECRStatusDetail>));
  }

  /**
   * Fetch Current Eta
   *
   * @summary Get current Eta list based on requestId and date
   * @param payload: { date: number; requestId: number }
   * @return Observable<Array<IECRStatusDetail> | never>
   */
  fetchCurrentEta(payload: { requestId: number; date: number }): Observable<Array<IECRStatusDetail> | never> {
    const params = new HttpParams()
      .set('date', payload.date.toString())
      .set('requestId', payload.requestId.toString());
    return this.http.get(NSUrlsConstants.ecrCurrentEtaUrl, { params }).pipe(map(response => response as Array<IECRStatusDetail>));
  }

  /**
   * Fetch Calendar
   *
   * @summary Fetch the calendar info
   * @param payload: Pick<IECRPattern, 'classCode' | 'locationId' | 'opStaCode' | 'patternName' | 'servingRoad'>
   * @param dates: { start: number; end: number }
   * @returns Observable<IECRCalendar | never>
   */
  fetchCalendar(
    payload: Pick<IECRPattern, 'classCode' | 'locationId' | 'opStaCode' | 'patternName' | 'servingRoad'>,
    dates: { start: number; end: number }
  ): Observable<IECRCalendar | never> {
    const params = new HttpParams()
      .set('classCode', payload.classCode)
      .set('customerPatternName', payload.patternName!)
      .set('endDate', dates.end.toString())
      .set('locationId', payload.locationId)
      .set('operatingStationCode', payload.opStaCode)
      .set('servingRoad', payload.servingRoad)
      .set('startDate', dates.start.toString());

    return this.http.get(NSUrlsConstants.ecrCalendarUrl, { params }).pipe(map(
      response => {
        const calendar = response as IECRCalendar;
        const cars: Array<IECRCar> = this.ecrSummaryUtils.getEvents(calendar, 2, calendar.startDate);
        return { ...calendar, cars } as IECRCalendar;
      }
    ));
  }

  /**
   * Submit Car Request
   *
   * @summary creates or updates car request
   * @param payload: { createServiceRequest?: IECRCarRequest; updateServiceRequest?: IECRCarRequest }
   * @returns Observable<Array<string> | never>
   */
  submitCarRequest(payload: { createServiceRequest?: IECRCarRequest; updateServiceRequest?: IECRCarRequest }): Observable<IECRSubmitResponse | never> {
    return this.http.post(NSUrlsConstants.ecrSubmitUrl, payload).pipe(map(response => response as IECRSubmitResponse));
  }

  /**
   * Submit Exceeded
   *
   * @summary Submits request for limit exceeded weeks
   * @param payload: Array<Record<string, unknown>>
   * @returns Observable<Array<string> | never>
   */
  submitExceeded(payload: Array<Record<string, unknown>>): Observable<IECRSubmitResponse | never> {
    return this.http.post(NSUrlsConstants.ecrSubmitExceededUrl, payload).pipe(map(response => response as IECRSubmitResponse));
  }

  /**
   * Cancel Car Request
   *
   * @summary Cancels a car request
   * @param payload: IECRCancelRequest
   * @returns Observable<IECRCancel | never>
   */
  cancelCarRequest(payload: IECRCancelRequest): Observable<IECRCancel | never> {
    return this.http.post(`${ NSUrlsConstants.ecrBaseUrl }/cancel`, payload).pipe(map(response => response as IECRCancel));
  }
}
